import { Component, DestroyRef, inject, OnInit, signal, computed, TemplateRef, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CatalogService, Catalog, Dataset } from '@htx/catalog';
import {
  FilterComponent,
  FilterConfigService,
  SlideOverPanelService,
  SortDropdownComponent,
  SortOption,
  SortDirection,
  sortItems
} from '@htx/core';
import { ServiceOfferEntityCardComponent } from '@htx/shared';
import { AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Observable, take, filter } from 'rxjs';
import { Policy, PolicyService, buildPolicyCardsForPolicy } from '@htx/policies';
import { DataModelService } from '../../../shared/services/data-model.service';
import {
  DataModelDialogComponent,
  DataModelDialogData
} from '../../../shared/components/data-model-dialog/data-model-dialog.component';

@Component({
  selector: 'app-catalog-overview-page',
  imports: [ServiceOfferEntityCardComponent, FilterComponent, TranslocoDirective, TranslocoPipe, AsyncPipe, MatIcon, SortDropdownComponent],
  templateUrl: './catalog-overview-page.component.html',
  styleUrl: './catalog-overview-page.component.scss'
})
export class CatalogOverviewPageComponent implements OnInit {
  readonly catalogService = inject(CatalogService);
  readonly dialog = inject(MatDialog);
  readonly filterConfigService = inject(FilterConfigService);
  readonly destroyRef = inject(DestroyRef);
  readonly panelService = inject(SlideOverPanelService);
  readonly policyService = inject(PolicyService);
  readonly dataModelService = inject(DataModelService);
  readonly transloco = inject(TranslocoService);

  catalog: Catalog = { id: '', datasets: [] };
  organizationName$: Observable<string | null> = this.catalogService.getOrganizationName$();
  loading$: Observable<boolean> = this.catalogService.loading$;
  hasSearched$: Observable<boolean> = this.catalogService.hasSearched$;

  public selectItemGroups = this.filterConfigService.getSelectItemGroups();
  public searchItemGroups = this.filterConfigService.getSearchItemGroups();

  /** Sort configuration */
  catalogDatasets = signal<Dataset[]>([]);

  datasetSortOptions: SortOption<Dataset>[] = [
    { field: (d: Dataset) => d.creationDate?.getTime() ?? 0, labelKey: 'sort.field.createdAt' },
    { field: (d: Dataset) => d.serviceOfferName ?? d.name ?? '', labelKey: 'sort.field.name' },
    { field: (d: Dataset) => d.serviceType ?? '', labelKey: 'sort.field.type' }
  ];
  datasetSortField = signal<SortOption<Dataset>['field']>(this.datasetSortOptions[0].field);
  datasetSortDirection = signal<SortDirection>('desc');

  sortedDatasets = computed(() =>
    sortItems(this.catalogDatasets(), { field: this.datasetSortField(), direction: this.datasetSortDirection() })
  );

  onDatasetSortChanged(event: { field: SortOption<Dataset>['field']; direction: SortDirection }): void {
    this.datasetSortField.set(event.field);
    this.datasetSortDirection.set(event.direction);
  }

  /** Template refs for the slide-over panel */
  detailBodyTpl = viewChild.required<TemplateRef<unknown>>('panelBody');
  detailFooterTpl = viewChild.required<TemplateRef<unknown>>('panelFooter');

  /** State for the currently previewed dataset in the panel */
  panelDataset = signal<Dataset | null>(null);
  panelLoading = signal(false);

  /** Whether the current dataset has a data model schema */
  panelHasDataModel = computed(() => {
    const entry = this.panelDataset();
    return !!entry?.type && this.dataModelService.hasSchema(entry.type);
  });

  /** Parsed keywords as a chip array */
  panelKeywords = computed(() => {
    const entry = this.panelDataset();
    if (!entry) return [];
    return this.formatKeywordsAsChips(entry.keywords);
  });

  /** Rich policy cards built from ODRL rules (or the flat constraint list) */
  panelPolicyCards = computed(() => {
    const entry = this.panelDataset();
    if (!entry?.policies?.length) return [];
    return entry.policies.flatMap(p => {
      const enriched = this.enrichPolicy(p);
      return buildPolicyCardsForPolicy(this.transloco, enriched);
    });
  });

  /** BPNL and protocol URL from catalog service */
  bpnl$: Observable<string | null> = this.catalogService.getBpnl$();
  protocolUrl$: Observable<string | null> = this.catalogService.getProtocolUrl$();

  /** Cached BPNL and protocol URL for the panel */
  panelBpnl = signal<string | null>(null);
  panelProtocolUrl = signal<string | null>(null);

  ngOnInit(): void {
    this.catalogService
      .getCatalog$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((catalog) => {
        this.catalog = catalog;
        this.catalog.datasets = this.catalog.datasets || [];
        this.catalogDatasets.set(this.catalog.datasets);
      });

    this.bpnl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => this.panelBpnl.set(v));
    this.protocolUrl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => this.panelProtocolUrl.set(v));
  }

  openCatalogDetailPage(catalogEntry: Dataset): void {
    this.panelLoading.set(true);
    this.panelDataset.set(null);

    this.panelService.open(
      {
        title: catalogEntry.serviceOfferName ?? catalogEntry.name ?? 'Dataset',
        subtitle: `ID: ${catalogEntry.id}`,
        initialWidth: 580
      },
      {
        bodyTemplate: this.detailBodyTpl(),
        footerTemplate: this.detailFooterTpl(),
        context: catalogEntry
      }
    );

    // The dataset is already fully loaded from the catalog search
    this.panelDataset.set(catalogEntry);
    this.panelLoading.set(false);
  }

  openDataModel(): void {
    const entry = this.panelDataset();
    if (!entry?.type || !this.dataModelService.hasSchema(entry.type)) return;

    this.dataModelService.getSchema(entry.type).pipe(
      take(1),
      filter((schema): schema is NonNullable<typeof schema> => !!schema)
    ).subscribe(schema => {
      this.dialog.open(DataModelDialogComponent, {
        data: { schema } as DataModelDialogData,
        width: '1080px',
        maxWidth: '95vw',
        maxHeight: '88vh',
        panelClass: 'data-model-dialog-panel',
      });
    });
  }

  private enrichPolicy(policy: Policy): Policy {
    return this.policyService.getPolicyById(policy.id) ?? policy;
  }

  private formatKeywordsAsChips(raw: unknown): string[] {
    if (Array.isArray(raw)) {
      return raw.filter((k: unknown): k is string => typeof k === 'string');
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }
}
